/**
 * The one client-side rule for how a role grant names its scope, shared by
 * every writer (AssignRoleModal, invitation flows) so the server's checks and
 * the client's resolver agree on what they are looking at.
 *
 *   workspace-wide  → scope_kind 'resource', scope_id = workspace id
 *                     (the convention 38 server-side checks already read;
 *                     'workspace' was a stray label the invitation paths wrote)
 *   org unit        → by depth, matching ScopeKind's hierarchy and the modal:
 *                     depth 0 → 'organization', 1 → 'department', ≥ 2 → 'team'
 *   nothing         → 'global' / '*'
 *
 * Mirrored server-side by public.org_unit_scope_kind (migration 722).
 * PRD: docs/9-exploration/PRD-delegable-grants.md FR-7 P0-c.
 */

import type { ScopeKind } from '../types/prioritization/permissions';

export type OrgUnitScopeKind = Extract<ScopeKind, 'organization' | 'department' | 'team'>;

/** Depth-based scope kind for an org-unit grant. */
export function orgUnitScopeKind(depth: number | null | undefined): OrgUnitScopeKind {
  const d = depth ?? 1;
  if (d <= 0) return 'organization';
  if (d === 1) return 'department';
  return 'team';
}

export interface GrantScope {
  scopeKind: ScopeKind;
  scopeId: string;
}

/**
 * Resolve the scope a grant should be written with.
 * `orgUnit` wins over `workspaceId`; neither → global wildcard.
 */
export function resolveGrantScope(params: {
  orgUnit?: { id: string; depth?: number | null } | null;
  workspaceId?: string | null;
}): GrantScope {
  if (params.orgUnit) {
    return { scopeKind: orgUnitScopeKind(params.orgUnit.depth), scopeId: params.orgUnit.id };
  }
  if (params.workspaceId) {
    return { scopeKind: 'resource', scopeId: params.workspaceId };
  }
  return { scopeKind: 'global', scopeId: '*' };
}
